import { useMutation } from '@apollo/client/react';

import type { Event } from '../../Data/types';
import { UPDATE_EVENT, GET_FILTERED_EVENTS } from '../../graphql/queries';
import { safeToNumber } from '../../helpers/safeNumberConversion/safeNumberConversion';
import useBuildGraphQLVariables from '../useBuildGraphQLVariables/useBuildGraphQLVariables';

function useUpdateEvent() {
  const filters = useBuildGraphQLVariables(); // Retrieve current filters for refetching

  const [updateEventMutation, { loading, error }] = useMutation(UPDATE_EVENT, {
    refetchQueries: [
      {
        query: GET_FILTERED_EVENTS,
        variables: { filters },
      },
    ],
  });

  const updateEvent = async (event: Event, currentUser: string) => {
    const variables = {
      id: event.id,
      event: {
        custom_hosting_group: event.hostingGroupOther,
        duration: event.recurrence?.duration,
        end_date_time: event.isRecurring
          ? event.recurrence!.endDate?.format('YYYY-MM-DDTHH:mm:ss')
          : event.eventDateTime?.end?.format('YYYY-MM-DDTHH:mm:ss'),
        end_date_time_utc: event.isRecurring ? event.recurrence!.endDate?.utc() : event.eventDateTime?.end?.utc(),
        event_details: event.description,
        event_format_id: event.eventFormat?.id || null,
        event_type_id: event.eventType?.id || 5, // Default to 5 if eventType is not provided
        event_type_other: event.eventTypeOther,
        external_office: event.externalLocation,
        hosting_group_other: event.hostingGroupOther,
        hosting_type: event.hostingType,
        is_all_day: event.allDay,
        is_cle: event.cle,
        is_client_event_or_celebration: event.clientEventOrCelebration,
        is_invite_only: event.inviteOnly,
        is_recurring: event.isRecurring,
        recurrence_pattern: event.recurrence?.recurrencePattern,
        sponsoring_orgnization: event.sponsorOrganization,
        start_date_time: event.isRecurring
          ? event.recurrence!.startDate?.format('YYYY-MM-DDTHH:mm:ss')
          : event.eventDateTime?.start?.format('YYYY-MM-DDTHH:mm:ss'),
        start_date_time_utc: event.isRecurring ? event.recurrence!.startDate?.utc() : event.eventDateTime?.start?.utc(),
        time_zone: event.timezone,
        title: event.title,
        updated_by: currentUser,
      },
      offices_in_events:
        event.officeLocation?.map((office) => ({
          event_id: event.id,
          office_id: office.id,
        })) || [],
      calendar_visibility:
        event.officeVisibility?.map((office) => ({
          event_id: event.id,
          office_id: office.id,
        })) || [],
      audience:
        event.audience
          ?.map((a) => {
            const audienceId = safeToNumber(a.id);
            return audienceId === null ? null : { event_id: event.id, audience_id: audienceId };
          })
          .filter((item) => item !== null) || [],
      practice_groups: event.practiceGroups
        .map((pg) => {
          const practiceGroupId = safeToNumber(pg.id);
          return practiceGroupId === null ? null : { event_id: event.id, practice_group_id: practiceGroupId };
        })
        .filter((item) => item !== null),
      industries: event.industries
        .map((i) => {
          const industryId = safeToNumber(i.id);
          return industryId === null ? null : { event_id: event.id, industry_id: industryId };
        })
        .filter((item) => item !== null),
      departments: event.departments
        .map((d) => {
          const departmentId = safeToNumber(d.id);
          return departmentId === null ? null : { event_id: event.id, department_id: departmentId };
        })
        .filter((item) => item !== null),
      firm_initiatives: event.firmInitiatives
        .map((fi) => {
          const firmInitiativeId = safeToNumber(fi.id);
          return firmInitiativeId === null ? null : { event_id: event.id, firm_initiative_id: firmInitiativeId };
        })
        .filter((item) => item !== null),
      outside_speakers:
        event.outsideSpeakers?.map((os) => {
          return {
            event_id: event.id,
            name: os.name,
            organization: os.organization,
            title: os.title,
          };
        }) || [],
      event_contacts:
        event.eventContact?.map((ec) => {
          return { event_id: event.id, user_id: ec.ADSystemID };
        }) || [],
      sponsoring_attorneys:
        event.sponsoringAttorney?.map((sa) => {
          return { event_id: event.id, user_id: sa.ADSystemID };
        }) || [],
      ml_speakers:
        event.mlSpeaker?.map((ms) => {
          return { event_id: event.id, user_id: ms.ADSystemID };
        }) || [],
    };

    return updateEventMutation({ variables });
  };

  return { updateEvent, loading, error };
}

export default useUpdateEvent;
